import React, { useState } from 'react';
import type { OnboardingStep } from '../../api/onboarding';

interface Props {
  steps: OnboardingStep[];
  currentStep: number;
  onContinue: () => void;
}

const DISMISS_KEY = 'cp_onboarding_checklist_dismissed_at';

export const OnboardingChecklist: React.FC<Props> = ({ steps, currentStep, onContinue }) => {
  const [dismissed, setDismissed] = useState(() => {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    // Dismiss for 7 days
    const dismissedAt = new Date(ts);
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return dismissedAt > sevenDaysAgo;
  });

  const completedCount = steps.filter((s) => s.completed).length;
  const totalCount = steps.length;
  const allComplete = completedCount === totalCount;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
    setDismissed(true);
  };

  if (dismissed || allComplete) return null;

  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{
        background: '#13161f',
        border: '1px solid rgba(139,92,246,0.2)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2">
          <span className="text-sm">🚀</span>
          <span className="text-sm font-semibold" style={{ color: '#f1f5f9' }}>Setup checklist</span>
        </div>
        <button
          onClick={handleDismiss}
          className="text-xs transition-colors hover:opacity-70"
          style={{ color: '#6b7280' }}
          title="Dismiss for 7 days"
        >
          ✕
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs" style={{ color: '#9ca3af' }}>
            {completedCount}/{totalCount} complete
          </span>
          <span className="text-xs font-medium" style={{ color: '#8b5cf6' }}>
            {Math.round(progress)}%
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full" style={{ background: '#1e2030' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #8b5cf6, #06b6d4)',
            }}
          />
        </div>
      </div>

      {/* Steps list */}
      <ul className="px-4 pb-3 space-y-1.5">
        {steps.map((step, idx) => (
          <li key={step.id} className="flex items-center gap-2.5">
            <span
              className="flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-xs"
              style={{
                background: step.completed ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)',
                border: step.completed ? '1px solid #10b981' : '1px solid rgba(255,255,255,0.1)',
                color: step.completed ? '#34d399' : '#6b7280',
              }}
            >
              {step.completed ? '✓' : idx + 1}
            </span>
            <span
              className="text-xs"
              style={{
                color: step.completed ? '#6b7280' : idx + 1 === currentStep ? '#e2e8f0' : '#9ca3af',
                textDecoration: step.completed ? 'line-through' : 'none',
              }}
            >
              {step.title}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {completedCount < totalCount && (
        <div className="px-4 pb-4">
          <button
            onClick={onContinue}
            className="w-full py-2 rounded-lg text-xs font-semibold transition-opacity hover:opacity-90"
            style={{
              background: 'rgba(139,92,246,0.2)',
              border: '1px solid rgba(139,92,246,0.4)',
              color: '#c4b5fd',
            }}
          >
            Continue setup →
          </button>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
